import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController, NavParams } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { EXTERNAL_KNOWLEDGE_BASE_TYPES, INTEGRATIONS, INTEGRATION_TYPES } from 'src/app/constants';
import { ENTITY_TYPES } from 'src/models/entities';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { GoogleApisService } from 'src/services/google-apis.service';
import { KnowledgeBaseScrapeJobsService } from 'src/services/knowledge-base-scrape-jobs.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { IntegrationInputModalComponent } from '../integration-input-modal/integration-input-modal';
import { IntegrationOutputModalComponent } from '../integration-output-modal/integration-output-modal';

const KNOWN_KNOWLEDGEBASE_DOMAINS = [
  'intercom.help',
  'help.intercom.com',
  'zendesk.com',
  'zendesk.help',
  'helpscoutdocs.com',
  'helpscout.com',
  'helpscout.net',
  'freshservice.com',
  'freshdesk.com',
  'github.com',
  'github.io',
];

@Component({
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, NgxTippyModule],
  selector: 'app-edit-knowledge-base-item',
  templateUrl: './edit-knowledge-base-item.component.html',
  styleUrls: ['./edit-knowledge-base-item.component.scss'],
})
export class EditKnowledgeBaseItemComponent implements OnInit {
  public KNOWLEDGE_BASE_TYPES = EXTERNAL_KNOWLEDGE_BASE_TYPES;

  @ViewChild('urlInput', { static: false }) urlInput;

  isLoading = false;
  type: string;
  url: string;
  connectionId: string;
  urlAllowlist: string;
  urlBlocklist: string;
  cookie: string;
  isRecursive: boolean = true;
  pageIdsAllowlist: string;
  pageIdsBlocklist: string;
  siteIds?: string;
  includeGlob?: string;
  sharedDriveIds: string;
  selectedDocumentIds?: string[];
  id?: string;
  showAdvanced = false;
  isSuperAdmin = false;
  isAddingExternalSource = false;
  userGroups?: { id: string; name?: string }[];
  channels?: { id: string; name?: string }[];
  integrations = [];

  showIntegrationList = false;

  internalDomains: string;
  currentDomain: string;

  title: string = '';
  autoSyncEnabled: boolean = false;
  autoSyncFrequency: 'daily' | 'weekly' = 'weekly';

  constructor(
    navParams: NavParams,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private knowledgeBaseScrapeJobsService: KnowledgeBaseScrapeJobsService,
    private googleApis: GoogleApisService,
    private apiService: ApiService,
    private searchablePopoverService: SearchablePopoverService,
  ) {
    this.id = navParams.get('id');
    this.type = navParams.get('type') ?? this.KNOWLEDGE_BASE_TYPES.WEB;
  }

  async ngOnInit(): Promise<void> {
    this.isSuperAdmin = this.authService.isSuperAdmin;

    this.integrations = this.filterIntegrationTypes(INTEGRATION_TYPES.KNOWLEDGEBASE);

    if (this.id) {
      await this.loadKnowledgeBaseScrapeJob();
    } else if (this.authService.tenant?.isInternalSupport) {
      this.selectInternalWikiView();
    } else {
      this.selectWebType();
    }
  }

  filterIntegrationTypes(type) {
    return Object.keys(INTEGRATIONS)
      .map((name) => {
        const integration = INTEGRATIONS[name];
        if (integration.type === type) {
          return integration;
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async getLookupValues(userGroupIds?: string[], channelIds?: string[]) {
    const lookups: Record<string, Record<string, string>> = userGroupIds.length || channelIds.length
      ? await this.apiService.postPromise('/saved-filters/get-lookup-values', {
          userGroupId: userGroupIds,
          customerId: channelIds,
        })
      : {};

    return {
      userGroups: userGroupIds?.map((id: string) => ({
        id,
        name: lookups.userGroupId?.[id],
      })),
      channels: channelIds?.map((id: string) => ({
        id,
        name: lookups.customerId?.[id],
      })),
    };
  }

  async loadKnowledgeBaseScrapeJob() {
    try {
      const knowledgeBaseScrapeJob = await this.knowledgeBaseScrapeJobsService.getById(this.id);

      this.type = knowledgeBaseScrapeJob.sourceType;

      this.url = knowledgeBaseScrapeJob.startUrl;
      this.connectionId = knowledgeBaseScrapeJob.connectionId;
      this.urlAllowlist = knowledgeBaseScrapeJob.sourceTypeConfig?.urlAllowlist?.join(',') ?? '';
      this.urlBlocklist = knowledgeBaseScrapeJob.sourceTypeConfig?.urlBlocklist?.join(',') ?? '';
      this.isRecursive = knowledgeBaseScrapeJob.sourceTypeConfig?.isRecursive;
      this.pageIdsAllowlist = knowledgeBaseScrapeJob.sourceTypeConfig?.pageIdsAllowlist?.join(',') ?? '';
      this.pageIdsBlocklist = knowledgeBaseScrapeJob.sourceTypeConfig?.pageIdsBlocklist?.join(',') ?? '';
      this.sharedDriveIds = knowledgeBaseScrapeJob.sourceTypeConfig?.sharedDriveIds?.join(',') ?? '';
      this.selectedDocumentIds = knowledgeBaseScrapeJob.sourceTypeConfig?.selectedDocumentIds ?? [];
      this.cookie = knowledgeBaseScrapeJob.sourceTypeConfig?.cookie;

      const userGroupIds = knowledgeBaseScrapeJob.userGroupIds ?? [];
      const channelIds = knowledgeBaseScrapeJob.channelIds ?? [];

      const lookupValues = await this.getLookupValues(userGroupIds, channelIds);

      this.userGroups = lookupValues.userGroups;
      this.channels = lookupValues.channels;

      this.title = knowledgeBaseScrapeJob.title || '';
      this.autoSyncEnabled = knowledgeBaseScrapeJob.autoSyncEnabled || false;
      this.autoSyncFrequency = knowledgeBaseScrapeJob.autoSyncFrequency || 'weekly';
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  async createKBIntegration(integration) {
    const modal = await this.modalCtrl.create({
      component: IntegrationInputModalComponent,
      componentProps: {
        integration,
      },
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data) {
      await this.modalCtrl.dismiss();
      await (this as any).modal?.dismiss();

      const output = await this.modalCtrl.create({
        component: IntegrationOutputModalComponent,
        componentProps: {
          integration,
          integrationOutput: data,
        },
        backdropDismiss: false,
      });
      await output.present();
    }
  }

  async submit() {
    if (this.id && this.type !== this.KNOWLEDGE_BASE_TYPES.WEB) {
      try {
        this.isLoading = true;

        const updateData: any = {
          title: this.title,
          autoSyncEnabled: this.autoSyncEnabled,
          autoSyncFrequency: this.autoSyncFrequency,
          userGroupIds: this.userGroups?.map((userGroup) => userGroup.id),
          channelIds: this.channels?.map((channel) => channel.id),
        };

        // For Google Drive sources, include sourceTypeConfig with sharedDriveIds
        if (this.type === this.KNOWLEDGE_BASE_TYPES.GOOGLE_DRIVE) {
          updateData.sourceTypeConfig = {
            sharedDriveIds: this.sharedDriveIds ? this.sharedDriveIds.replace(/\s/g, '').split(',') : [],
            selectedDocumentIds: this.selectedDocumentIds || [],
          };
        }

        await this.knowledgeBaseScrapeJobsService.updatePartial(this.id, updateData);

        this.dismiss();
      } catch (error) {
        await this.msgSrvc.showError(error);
      } finally {
        this.isLoading = false;
      }
      return;
    }

    try {
      if (!this.url?.startsWith('http')) {
        this.url = `https://${this.url}`;
      }

      const url = new URL(this.url);
      const host = url.host;

      const allowedDomains = [...KNOWN_KNOWLEDGEBASE_DOMAINS, ...(this.authService.tenant?.allowedEmailDomains ?? [])];

      let allowAdd = false;
      allowedDomains.forEach((domain: string) => {
        if (host.includes(domain)) {
          allowAdd = true;
        }
      });

      if (!allowAdd && !this.isSuperAdmin && this.authService.user.email !== 'tom@unthread.io') {
        this.isAddingExternalSource = true;
        this.currentDomain = host;
        this.internalDomains = allowedDomains.join(', ');
        return;
      }

      this.isLoading = true;

      try {
        await this.knowledgeBaseScrapeJobsService.create({
          id: this.id,
          startUrl: this.url,
          connectionId: this.connectionId,
          userGroupIds: this.userGroups?.map((userGroup) => userGroup.id),
          channelIds: this.channels?.map((channel) => channel.id),
          sourceType: this.KNOWLEDGE_BASE_TYPES.WEB,
          sourceTypeConfig: {
            urlAllowlist: this.urlAllowlist ? this.urlAllowlist.replace(/\s/g, '').split(',') : [],
            urlBlocklist: this.urlBlocklist ? this.urlBlocklist.replace(/\s/g, '').split(',') : [],
            isRecursive: this.isRecursive,
            cookie: this.cookie,
          },
          projectId: this.authService.selectedProject?.id ?? null,
          title: this.title,
        });

        await this.msgSrvc.show(this.id ? '✔️ Updated Source' : '✔️ Starting sync');
        this.dismiss();
      } catch (error) {
        await this.msgSrvc.showError(error);
      } finally {
        this.isLoading = false;
      }
    } catch (e) {
      this.msgSrvc.show('Whoops! Please enter a valid URL.');
      this.isLoading = false;
    }
  }

  async delete() {
    if (!this.id) return;

    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader: `This source and its synced articles can't be undone`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.knowledgeBaseScrapeJobsService.delete(this.id);
              await this.msgSrvc.show('Source deleted.');
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.show(`Unable to remove source: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }

  dismiss(object?: any) {
    this.modalCtrl.dismiss(object);
  }

  async contactSupport() {
    (window as any).$unthread.inAppChat('toggle', 'show');
  }

  async openGoogleDriveFilePicker() {
    const tokenResponse: any = await this.apiService.getPromise(`/me/tenant/tokens/google-drive/${this.id}`);

    const docs = await this.googleApis.showPicker(tokenResponse.token);
    if (!docs) {
      return;
    }

    const ids = docs.map((doc) => doc.id);
    this.selectedDocumentIds = this.selectedDocumentIds.concat(ids);
    this.submit();
  }

  async addUserGroup() {
    this.searchablePopoverService.entities({
      event,
      type: ENTITY_TYPES.USER_GROUP,
      excludeIds: this.userGroups?.map((userGroup) => userGroup.id),
      callback: async (id: string, name: string, object: any) => {
        if (id) {
          this.userGroups = [...(this.userGroups ?? []), { id, name }];
        }
      },
    });
  }

  removeUserGroup(id: string) {
    this.userGroups = this.userGroups?.filter((userGroup) => userGroup.id !== id);
  }

  addChannel() {
    this.searchablePopoverService.customers({
      event,
      excludeIds: this.channels?.map((channel) => channel.id),
      callback: async (id: string, name: string) => {
        if (id && !this.channels?.some((c) => c.id === id)) {
          this.channels = [...(this.channels ?? []), { id, name }];
        }
      },
    });
  }

  removeChannel(channelIdToRemove: string) {
    this.channels = this.channels.filter((channel) => channel.id !== channelIdToRemove);
  }

  selectWebType() {
    this.type = this.KNOWLEDGE_BASE_TYPES.WEB;
    this.showIntegrationList = false;
    this.showAdvanced = false;
    setTimeout(() => {
      this.urlInput?.setFocus();
    }, 100);
  }

  selectInternalWikiView() {
    this.showIntegrationList = true;
    this.showAdvanced = false;
  }
}
